import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {dataToolSchemas} from '../../utils/schemas';

const toolName = 'update-page-metadata' as const;

const seoSchema = z
  .object({
    title: z.string().optional().describe('SEO title for the page'),
    description: z.string().optional().describe('SEO description for the page'),
  })
  .optional()
  .describe('SEO metadata for the page');

const openGraphSchema = z
  .object({
    title: z.string().optional().describe('Open Graph title'),
    titleCopied: z
      .boolean()
      .optional()
      .describe('Whether the Open Graph title is copied from the page title'),
    description: z.string().optional().describe('Open Graph description'),
    descriptionCopied: z
      .boolean()
      .optional()
      .describe(
        'Whether the Open Graph description is copied from the SEO description'
      ),
  })
  .optional()
  .describe('Open Graph metadata for social media sharing');

const argumentZod = z.object({
  pageId: dataToolSchemas.pageId,
  localeId: dataToolSchemas.localeId.optional(),
  title: z.string().optional().describe('The page title'),
  slug: z.string().optional().describe('The page URL slug'),
  seo: seoSchema,
  openGraph: openGraphSchema,
});

export const updatePageMetadataManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Update page metadata',
  family: 'pages',
  llmTool: {
    name: toolName,
    description:
      'Updates the metadata for a specific page by ID. Can update title, slug, SEO settings, and Open Graph data. Supports locale-specific updates.',
    inputSchema: argumentZod,
  },
  type: 'server',
  permission: [
    {
      resource: 'SitePageSettings',
      action: 'manage',
      resolveResources: (args) => [{type: 'SitePageSettings', id: args.pageId}],
    },
  ],
  confirmBeforeExecuting: false,
});

export type UpdatePageMetadataArgumentType = z.infer<typeof argumentZod>;
